import React from 'react';
import { motion } from 'framer-motion';
import { Button, Section } from './index';

/**
 * Hero section component with coffee-themed design
 * Features BigCash Coffee Token branding and call-to-action buttons
 */
const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };
  
  return (
    <Section
      id="hero"
      fullHeight={true}
      className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 via-yellow-50 to-red-50"
    >
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 via-orange-400/20 to-red-400/20 animate-gradient-x"></div>
      
      {/* Glassmorphism Overlay */}
      <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]"></div>
      {/* Enhanced Background Decorations */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating Coffee Elements */}
        <div className="absolute top-20 left-10 w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full opacity-30 animate-float shadow-lg"></div>
        <div className="absolute top-40 right-20 w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-full opacity-40 animate-bounce-slow shadow-md"></div>
        <div className="absolute bottom-32 left-20 w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full opacity-25 animate-float shadow-xl"></div>
        <div className="absolute bottom-20 right-10 w-6 h-6 bg-gradient-to-br from-red-400 to-pink-500 rounded-full opacity-35 animate-bounce-slow shadow-lg"></div>
        
        {/* Additional Floating Elements */}
        <div className="absolute top-1/3 left-1/4 w-4 h-4 bg-gradient-to-br from-amber-300 to-yellow-400 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-2/3 right-1/3 w-10 h-10 bg-gradient-to-br from-orange-300 to-red-400 rounded-full opacity-25 animate-float"></div>
      </div>
      
      <motion.div
        className="text-center relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* BGC Logo */}
          <motion.div 
            className="mb-8"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="flex items-center justify-center">
              <img 
              src="/bgc-logo.svg" 
              alt="BigCash Coffee Token Logo" 
              className="w-32 h-48 object-contain"
            />
            </div>
          </motion.div>
        
        {/* Main Heading */}
        <motion.h1
          variants={itemVariants}
          className="text-6xl md:text-8xl lg:text-9xl font-black font-display mb-8 leading-tight"
        >
          <span className="bg-gradient-to-r from-amber-600 via-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent drop-shadow-2xl">
            BigCash
          </span>
          <br />
          <span className="bg-gradient-to-r from-coffee-800 via-amber-800 to-orange-800 bg-clip-text text-transparent drop-shadow-lg">
            Coffee Token
          </span>
        </motion.h1>
        
        {/* Enhanced Taglines */}
        <motion.div
          variants={itemVariants}
          className="mb-12 space-y-4"
        >
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-amber-700 to-orange-700 bg-clip-text text-transparent">
            Malaysia's First Coffee Token
          </p>
          
          <p className="text-xl md:text-2xl text-coffee-600 font-medium max-w-3xl mx-auto leading-relaxed">
            More Coffee, More BigCash - Revolutionizing the Coffee Industry
          </p>
          
          <div className="flex items-center justify-center space-x-4 text-amber-600">
            <span className="text-2xl">☕</span>
            <span className="text-lg font-semibold">Premium Quality</span>
            <span className="text-2xl">💎</span>
            <span className="text-lg font-semibold">Blockchain Powered</span>
            <span className="text-2xl">🚀</span>
          </div>
        </motion.div>
        
        {/* Call-to-Action Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Button
            variant="primary"
            size="lg"
            className="w-full sm:w-auto min-w-[200px]"
          >
            Buy Token
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            href="https://etherscan.io/token/0xd043f70ff0634167db1149b15e5668f5be52d72c"
            className="w-full sm:w-auto min-w-[200px]"
          >
            View on Etherscan
          </Button>
        </motion.div>
        
        {/* Token Address */}
        <motion.div
          variants={itemVariants}
          className="mt-12 p-4 bg-white/80 backdrop-blur-sm rounded-lg border border-coffee-200 max-w-md mx-auto"
        >
          <p className="text-sm text-coffee-600 mb-2">Contract Address:</p>
          <p className="text-xs font-mono text-coffee-800 break-all">
            0xd043f70ff0634167db1149b15e5668f5be52d72c
          </p>
        </motion.div>
        
        {/* Scroll Indicator */}
        <motion.div
          variants={itemVariants}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-coffee-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-coffee-400 rounded-full mt-2 animate-bounce"></div>
          </div>
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default Hero;